import { useState, useRef } from 'react';
import { Upload } from 'lucide-react';

interface Props {
  entityType: string;
  onImport: (file: File) => Promise<any>;
  onClose: () => void;
}

export default function ImportModal({ entityType, onImport, onClose }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      const ext = f.name.split('.').pop()?.toLowerCase();
      if (!['csv', 'xls', 'xlsx'].includes(ext || '')) {
        setError('Поддерживаются только форматы CSV, XLS, XLSX');
        return;
      }
      setFile(f);
      setError('');
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const res = await onImport(file);
      setResult(res.data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Ошибка импорта');
    } finally {
      setLoading(false);
    }
  };

  const entityLabels: Record<string, string> = {
    companies: 'Компании',
    contacts: 'Контакты',
    leads: 'Лиды',
    deals: 'Сделки',
    tasks: 'Задачи',
  };

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>Импорт {entityLabels[entityType] || entityType}</h3>
          <button onClick={onClose} style={{ background: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div className="modal-body">
          {result ? (
            <div>
              <div style={{ padding: '12px 16px', marginBottom: 16, background: 'var(--color-success-bg, #efe)', color: 'var(--color-success, #060)', borderRadius: 6, fontSize: 13 }}>
                Импорт завершён: создано {result.created}, пропущено {result.skipped} из {result.total}
              </div>
              {result.errors?.length > 0 && (
                <div style={{ maxHeight: 200, overflow: 'auto', fontSize: 12, color: 'var(--color-text-secondary)', background: 'var(--color-bg-secondary, #f7fafc)', borderRadius: 6, padding: 12 }}>
                  {result.errors.map((err: string, i: number) => <div key={i}>{err}</div>)}
                </div>
              )}
            </div>
          ) : (
            <>
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                Загрузите файл в формате CSV, XLS или XLSX. Первая строка должна содержать заголовки столбцов.
              </p>
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed var(--color-border-soft, #e2e8f0)',
                  borderRadius: 8, padding: 32, textAlign: 'center', cursor: 'pointer',
                  background: 'var(--color-bg-secondary, #f7fafc)',
                }}
              >
                <Upload size={32} style={{ color: 'var(--color-text-tertiary, #a0aec0)', marginBottom: 8 }} />
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                  {file ? file.name : 'Нажмите для выбора файла'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary, #a0aec0)' }}>
                  CSV, XLS, XLSX (макс. 10 МБ)
                </div>
              </div>
              <input ref={fileInputRef} type="file" accept=".csv,.xls,.xlsx" onChange={handleFileChange} style={{ display: 'none' }} />
              {error && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--color-danger-bg, #fee)', color: 'var(--color-danger, #c00)', borderRadius: 6, fontSize: 13 }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {result ? 'Закрыть' : 'Отмена'}
          </button>
          {!result && (
            <button className="btn btn-primary" disabled={!file || loading} onClick={handleImport}>
              {loading ? 'Импорт...' : 'Импортировать'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
